import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import {
  Layers, Loader2, RefreshCw, Lock, TrendingUp, Target, Sparkles,
  ChevronRight, CheckCircle2, AlertTriangle, X, MapPin, FileText, Link2, HelpCircle,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const fetchTopics = () => api.get("/admin/topics").then((r) => r.data);
const rebuildTopics = () => api.post("/admin/topics/rebuild").then((r) => r.data);
const fetchTopic = (slug) => api.get(`/admin/topics/${slug}`).then((r) => r.data);

const STATUS_META = {
  DOMINATING: { cls: "bg-green-100 text-green-800 border-green-300", bar: "bg-green-500" },
  STRONG: { cls: "bg-emerald-50 text-emerald-700 border-emerald-200", bar: "bg-emerald-500" },
  DEVELOPING: { cls: "bg-amber-50 text-amber-700 border-amber-200", bar: "bg-amber-500" },
  WEAK: { cls: "bg-orange-50 text-orange-700 border-orange-200", bar: "bg-orange-500" },
  "CRITICAL GAP": { cls: "bg-red-50 text-red-700 border-red-200", bar: "bg-red-500" },
};

function Bar({ value, tone = "bg-cool" }) {
  return (
    <div className="h-2 w-full rounded-full bg-slate-100 overflow-hidden">
      <div className={`h-full rounded-full ${tone}`} style={{ width: `${Math.max(2, value)}%`, transition: "width .4s ease" }} />
    </div>
  );
}

function ExecCard({ label, value, sub, tone = "text-navy" }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className={`text-2xl font-bold ${tone}`}>{value}</div>
      <div className="text-xs font-semibold text-slate-600 mt-0.5">{label}</div>
      {sub && <div className="text-[11px] text-slate-400 mt-0.5">{sub}</div>}
    </div>
  );
}

function LockedTile({ label }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-md border border-dashed border-slate-300 bg-slate-50 px-1.5 py-0.5 text-[11px] font-medium text-slate-400" title="Connect a data source to unlock">
      <Lock className="h-3 w-3" /> {label} —
    </span>
  );
}

function TopicDetail({ slug, onClose }) {
  const { data, isLoading } = useQuery({ queryKey: ["topic", slug], queryFn: () => fetchTopic(slug), enabled: !!slug });
  return (
    <Dialog open={!!slug} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="topic-detail-dialog">
        {isLoading || !data ? (
          <div className="flex items-center justify-center py-16 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-navy">
                <Layers className="h-5 w-5 text-launch" /> {data.name}
                <span className={`ml-1 rounded-full border px-2 py-0.5 text-[11px] font-bold ${STATUS_META[data.status]?.cls}`}>{data.status}</span>
              </DialogTitle>
            </DialogHeader>
            <DialogDescription className="sr-only">Topic ownership detail, coverage, customer questions and recommended next action for {data.name}.</DialogDescription>
            <p className="text-sm text-slate-500 -mt-1">{data.description}</p>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3">
              <ExecCard label="Topical Gravity" value={data.topical_gravity} tone="text-launch" />
              <ExecCard label="Coverage" value={`${data.coverage_pct}%`} />
              <ExecCard label="Customer Qs" value={`${data.customer_question_pct}%`} />
              <ExecCard label="Content Gap" value={`${data.content_gap_pct}%`} tone="text-red-600" />
            </div>

            <div className="mt-4 rounded-lg border border-launch/30 bg-launch/5 p-3">
              <div className="text-[11px] font-bold uppercase tracking-wide text-launch flex items-center gap-1"><Target className="h-3.5 w-3.5" /> Recommended next action</div>
              <div className="text-sm text-navy font-medium mt-1">{data.recommended_action}</div>
            </div>

            {/* Topic map (tree) */}
            <div className="mt-5">
              <h4 className="text-sm font-bold text-navy mb-2 flex items-center gap-1"><Layers className="h-4 w-4" /> Topic Map</h4>
              <div className="rounded-lg border border-slate-200 p-3 text-sm">
                <div className="font-semibold text-navy">{data.name}</div>
                <div className="mt-1 space-y-2">
                  {(data.subtopics || []).map((s, i) => (
                    <div key={i} className="pl-3 border-l-2 border-slate-200">
                      <div className="font-medium text-slate-700">{s.name}</div>
                      <ul className="mt-1 space-y-1">
                        {(s.questions || []).map((q, j) => {
                          const covered = (data.covered_questions || []).includes(q);
                          return (
                            <li key={j} className="flex items-start gap-1.5 text-[13px]">
                              {covered ? <CheckCircle2 className="h-3.5 w-3.5 text-green-500 mt-0.5 shrink-0" /> : <AlertTriangle className="h-3.5 w-3.5 text-orange-400 mt-0.5 shrink-0" />}
                              <span className={covered ? "text-slate-600" : "text-orange-700 font-medium"}>{q}</span>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4 mt-5">
              <div>
                <h4 className="text-sm font-bold text-navy mb-2 flex items-center gap-1"><FileText className="h-4 w-4" /> Pages covering this topic</h4>
                <div className="space-y-1.5 text-[13px]">
                  {data.parent_page && (
                    <a href={data.parent_page.url} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-cool hover:underline"><ChevronRight className="h-3.5 w-3.5" /> {data.parent_page.label} <span className="text-slate-400">(pillar)</span></a>
                  )}
                  {(data.supporting_pages || []).map((p, i) => (
                    <a key={i} href={p.url} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-slate-600 hover:text-cool hover:underline"><ChevronRight className="h-3.5 w-3.5" /> {p.label}</a>
                  ))}
                  {!data.parent_page && (data.supporting_pages || []).length === 0 && <p className="text-slate-400">No pages yet — this is a content gap.</p>}
                </div>
                {data.local && (
                  <div className="mt-3">
                    <h4 className="text-xs font-bold text-navy mb-1 flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> Local pages ({data.local_page_count})</h4>
                    <div className="flex flex-wrap gap-1">
                      {(data.local_pages || []).map((p, i) => (
                        <a key={i} href={p.url} target="_blank" rel="noreferrer" className="rounded border border-slate-200 px-1.5 py-0.5 text-[11px] text-slate-500 hover:text-cool">{p.label}</a>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <div>
                <h4 className="text-sm font-bold text-navy mb-2 flex items-center gap-1"><HelpCircle className="h-4 w-4" /> Real customer questions ({data.customer_question_count})</h4>
                {(data.customer_questions || []).length ? (
                  <ul className="space-y-1.5 text-[13px] max-h-56 overflow-y-auto">
                    {data.customer_questions.map((c, i) => (
                      <li key={i} className="flex items-start gap-1.5">
                        {c.covered ? <CheckCircle2 className="h-3.5 w-3.5 text-green-500 mt-0.5 shrink-0" /> : <AlertTriangle className="h-3.5 w-3.5 text-orange-400 mt-0.5 shrink-0" />}
                        <span className="text-slate-600">{c.text}</span>
                      </li>
                    ))}
                  </ul>
                ) : <p className="text-slate-400 text-[13px]">No matching customer questions found in leads/timeline yet.</p>}
                <div className="mt-3 flex items-center gap-2 text-[12px] text-slate-500"><Link2 className="h-3.5 w-3.5" /> {data.inbound_links} inbound internal links · {data.supporting_count} supporting pages</div>
              </div>
            </div>

            <div className="mt-4 flex flex-wrap gap-2 border-t border-slate-100 pt-3">
              <span className="text-[11px] text-slate-400 self-center">External signals (connect a source):</span>
              <LockedTile label="Google Visibility" />
              <LockedTile label="AI Visibility" />
              <LockedTile label="Competitor" />
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function TopicOwnership() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["topics"], queryFn: fetchTopics });
  const [detail, setDetail] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const rebuild = useMutation({ mutationFn: rebuildTopics, onSuccess: (d) => qc.setQueryData(["topics"], d) });

  if (isLoading || !data) return <div className="flex items-center justify-center py-24 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  const { topics, summary } = data;
  const shown = statusFilter === "all" ? topics : topics.filter((t) => t.status === statusFilter);

  const execCards = [
    { key: "all", label: "Total Topics", value: summary.total_topics, tone: "text-navy" },
    { key: "DOMINATING", label: "Dominating", value: summary.dominating, tone: "text-green-600" },
    { key: "STRONG", label: "Strong", value: summary.strong, tone: "text-emerald-600" },
    { key: "DEVELOPING", label: "Developing", value: summary.developing, tone: "text-amber-600" },
    { key: "WEAK", label: "Weak", value: summary.weak, tone: "text-orange-600" },
    { key: "CRITICAL GAP", label: "Critical Gaps", value: summary.critical_gaps, tone: "text-red-600" },
  ];

  return (
    <div data-testid="topic-ownership-root">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-navy flex items-center gap-2"><Layers className="h-6 w-6 text-launch" /> Topic Ownership</h1>
          <p className="text-slate-500 mt-1 max-w-3xl">Which HVAC topics do we own in Central Florida, which are we losing, and what should we build next? Each topic gets a proprietary <strong>Topical Gravity Score (0–100)</strong> computed from real coverage, customer questions, internal linking and content depth — not raw page count.</p>
        </div>
        <button onClick={() => rebuild.mutate()} disabled={rebuild.isPending} data-testid="topics-rebuild-btn" className="inline-flex items-center gap-2 bg-navy text-white text-sm font-semibold px-4 py-2 rounded-lg disabled:opacity-60">
          {rebuild.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Rebuild
        </button>
      </div>

      {/* Executive summary */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mt-5" data-testid="topic-exec-cards">
        {execCards.map((c) => (
          <button key={c.key} onClick={() => setStatusFilter(c.key)} className={`text-left rounded-xl border p-4 transition ${statusFilter === c.key ? "border-launch ring-1 ring-launch/30" : "border-slate-200 hover:border-slate-300"} bg-white`}>
            <div className={`text-2xl font-bold ${c.tone}`}>{c.value}</div>
            <div className="text-xs font-semibold text-slate-600 mt-0.5">{c.label}</div>
          </button>
        ))}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3">
        <ExecCard label="Avg Topical Gravity" value={summary.avg_gravity} tone="text-launch" sub="Portfolio authority" />
        <ExecCard label="Avg Coverage" value={`${summary.avg_coverage}%`} />
        <ExecCard label="Avg Customer-Q Coverage" value={`${summary.avg_question_coverage}%`} />
        <ExecCard label="Topics With Gaps" value={summary.content_gap_topics} tone="text-red-600" sub="Content gap ≥ 40%" />
      </div>

      {/* What should we do next */}
      <div className="mt-6 rounded-xl border border-launch/30 bg-gradient-to-br from-launch/5 to-white p-5" data-testid="what-next-panel">
        <h2 className="text-lg font-bold text-navy flex items-center gap-2"><Sparkles className="h-5 w-5 text-launch" /> What should we do next?</h2>
        <p className="text-xs text-slate-500 mb-3">The highest-value actions to grow topic ownership, ranked by business value, demand and content gap.</p>
        <ol className="space-y-2">
          {summary.what_next.map((w, i) => (
            <li key={w.slug} className="flex items-start gap-3 rounded-lg border border-slate-200 bg-white p-3">
              <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-launch text-white text-xs font-bold">{i + 1}</span>
              <div className="min-w-0 flex-1">
                <button onClick={() => setDetail(w.slug)} className="text-sm font-semibold text-navy hover:text-launch text-left">{w.topic}
                  <span className={`ml-2 rounded-full border px-1.5 py-0.5 text-[10px] font-bold ${STATUS_META[w.status]?.cls}`}>{w.status}</span>
                </button>
                <p className="text-[13px] text-slate-600">{w.action}</p>
              </div>
              <span className="shrink-0 text-right"><span className="text-[10px] text-slate-400 block">opportunity</span><span className="text-sm font-bold text-launch">{w.opportunity_score}</span></span>
            </li>
          ))}
        </ol>
      </div>

      {/* Topic ownership table */}
      <div className="mt-6 rounded-xl border border-slate-200 bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="topic-ownership-table">
            <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3">Topic</th>
                <th className="px-4 py-3 w-40">Topical Gravity</th>
                <th className="px-4 py-3">Coverage</th>
                <th className="px-4 py-3">Customer Qs</th>
                <th className="px-4 py-3">Gap</th>
                <th className="px-4 py-3">Value</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">External</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {shown.map((t) => (
                <tr key={t.slug} className="hover:bg-slate-50 cursor-pointer" onClick={() => setDetail(t.slug)} data-testid={`topic-row-${t.slug}`}>
                  <td className="px-4 py-3">
                    <div className="font-semibold text-navy">{t.name}</div>
                    <div className="text-[11px] text-slate-400">{t.supporting_count} pages · {t.inbound_links} links{t.local ? ` · ${t.local_page_count} local` : ""}</div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className="w-7 text-right font-bold text-navy">{t.topical_gravity}</span>
                      <div className="flex-1"><Bar value={t.topical_gravity} tone={STATUS_META[t.status]?.bar} /></div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-slate-600">{t.coverage_pct}%</td>
                  <td className="px-4 py-3 text-slate-600">{t.customer_question_pct}%</td>
                  <td className="px-4 py-3"><span className={t.content_gap_pct >= 40 ? "text-red-600 font-semibold" : "text-slate-500"}>{t.content_gap_pct}%</span></td>
                  <td className="px-4 py-3 text-slate-600">{t.commercial_value}</td>
                  <td className="px-4 py-3"><span className={`rounded-full border px-2 py-0.5 text-[11px] font-bold whitespace-nowrap ${STATUS_META[t.status]?.cls}`}>{t.status}</span></td>
                  <td className="px-4 py-3"><span className="inline-flex items-center gap-1 text-[11px] text-slate-400"><Lock className="h-3 w-3" /> —</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Top topics to own next */}
      <div className="mt-6 rounded-xl border border-slate-200 bg-white p-5" data-testid="to-own-next-panel">
        <h2 className="text-lg font-bold text-navy flex items-center gap-2"><TrendingUp className="h-5 w-5 text-launch" /> Top 10 Topics to Own Next</h2>
        <p className="text-xs text-slate-500 mb-3">Ranked by business value + customer demand + content gap + upside.</p>
        <div className="grid sm:grid-cols-2 gap-2">
          {summary.to_own_next.map((t, i) => (
            <button key={t.slug} onClick={() => setDetail(t.slug)} className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 p-3 text-left hover:border-launch">
              <div className="min-w-0"><span className="text-xs text-slate-400 mr-1">#{i + 1}</span><span className="font-semibold text-navy">{t.name}</span>
                <div className="text-[12px] text-slate-500 truncate">{t.recommended_action}</div>
              </div>
              <span className="shrink-0 text-right"><span className="text-sm font-bold text-launch">{t.opportunity_score}</span><span className="block text-[10px] text-slate-400">gap {t.content_gap_pct}%</span></span>
            </button>
          ))}
        </div>
      </div>

      <p className="mt-4 text-[11px] text-slate-400 flex items-center gap-1"><Lock className="h-3 w-3" /> Google Visibility, AI Visibility and Competitor comparison arrive in later phases — no numbers are invented until a real source is connected.</p>

      {detail && <TopicDetail slug={detail} onClose={() => setDetail(null)} />}
    </div>
  );
}
